import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { assignmentsApi } from '../../../services/apiV2';

type StudentRow = {
  student_id: number;
  student_name: string;
  assignment_id?: number;
  section_id?: number;
  section_name?: string;
  score_percent?: number;
  correct_count?: number;
  objective_item_count?: number;
  status?: string;
  ratings?: string[];
};

type ItemResult = {
  step_id: number;
  title: string;
  order: number;
  answered_count: number;
  correct_count: number;
  incorrect_count: number;
  unanswered_count: number;
  correct_percent: number | null;
};

type ClassSummary = {
  assignment_id: number;
  assessment_title: string;
  assessment_stage: string;
  section_name: string;
  section_count?: number;
  selected_assignment_ids?: number[];
  matching_assignments?: Array<{
    assignment_id: number;
    section_id: number;
    section_name: string;
    assigned_at: string;
    is_active: boolean;
  }>;
  section_summaries?: Array<{
    ai_label: string;
    assignment_id: number;
    section_id: number;
    section_name: string;
    targeted_count: number;
    completed_count: number;
    objective_average_percent: number | null;
    below_threshold_count: number;
    written_support_count: number;
    incomplete_count: number;
  }>;
  threshold_percent: number;
  completion: {
    targeted_count: number;
    completed_count: number;
    in_progress_count: number;
    not_started_count: number;
  };
  objective_overview: {
    item_count: number;
    students_scored_count: number;
    average_percent: number | null;
  };
  strengths: ItemResult[];
  priority_areas: ItemResult[];
  students_below_threshold: StudentRow[];
  written_quality: Record<'exceeds' | 'meets' | 'approaching' | 'beginning' | 'not_reviewed', number>;
  students_needing_written_support: StudentRow[];
  students_with_unreviewed_writing: StudentRow[];
  incomplete_students: StudentRow[];
  ai_narrative: {
    enabled: boolean;
    aggregate_only: boolean;
    identity_sent: boolean;
    student_response_text_sent: boolean;
  };
};

type ActionPlanStudent = StudentRow & { student_key: string };
type ActionPlanGroup = {
  group_id: string;
  kind: string;
  title: string;
  learning_need: string;
  reason: string;
  objectives: Array<{ step_id: number; title: string }>;
  students: ActionPlanStudent[];
  suggested_delivery: 'live_small_group' | 'independent_lesson_runner';
  delivery_options: Array<'live_small_group' | 'independent_lesson_runner'>;
  prior_lessons: Array<{
    lesson_plan_id: number;
    title: string;
    run_count: number;
    last_run_at: string;
    match_terms: string[];
  }>;
  gpt_prompt_template: string;
};
type AssessmentActionPlan = {
  groups: ActionPlanGroup[];
  individual_follow_up: ActionPlanStudent[];
  incomplete_students: StudentRow[];
  same_hook_only: boolean;
  hook: { id: number; title: string };
  advisory_only: boolean;
  auto_assigned: boolean;
  privacy: { prompt_identity_sent: boolean; student_response_text_sent: boolean };
};

const humanize = (value?: string): string => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatPercent = (value: number | null | undefined): string => (
  typeof value === 'number' ? `${value}%` : 'Not available'
);

export const V2AssessmentClassSummary: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_queue');
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const numericAssignmentId = Number(assignmentId);
  const [summary, setSummary] = useState<ClassSummary | null>(null);
  const [thresholdInput, setThresholdInput] = useState('70');
  const [appliedThreshold, setAppliedThreshold] = useState(70);
  const [pendingAssignmentIds, setPendingAssignmentIds] = useState<number[]>([numericAssignmentId]);
  const [appliedAssignmentIds, setAppliedAssignmentIds] = useState<number[]>([numericAssignmentId]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNarrative, setAiNarrative] = useState('');
  const [aiNotice, setAiNotice] = useState('');
  const [actionPlan, setActionPlan] = useState<AssessmentActionPlan | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [groupSelections, setGroupSelections] = useState<Record<string, string[]>>({});
  const [groupDelivery, setGroupDelivery] = useState<Record<string, ActionPlanGroup['suggested_delivery']>>({});
  const [copyNotice, setCopyNotice] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!Number.isFinite(numericAssignmentId)) {
        setError('Assessment is missing.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const payload = await assignmentsApi.classSummary(numericAssignmentId, appliedThreshold, appliedAssignmentIds);
        setSummary(payload as ClassSummary);
      } catch (err: any) {
        setSummary(null);
        setError(err?.response?.data?.error || 'Could not load the class summary.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [appliedAssignmentIds, appliedThreshold, numericAssignmentId]);

  const applyThreshold = (event: FormEvent) => {
    event.preventDefault();
    const value = Number(thresholdInput);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setError('Enter a threshold from 0 to 100.');
      return;
    }
    setError('');
    setAppliedThreshold(value);
    setAppliedAssignmentIds([...pendingAssignmentIds].sort((a, b) => a - b));
    setAiNarrative('');
    setAiNotice('');
    setActionPlan(null);
    setCopyNotice('');
  };

  const generateNarrative = async () => {
    if (!summary) return;
    if (aiNarrative.trim() && !window.confirm('Replace the current narrative with a new AI draft?')) return;
    setAiLoading(true);
    setError('');
    setAiNotice('');
    try {
      const response = await assignmentsApi.draftClassSummary(summary.assignment_id, appliedThreshold, appliedAssignmentIds);
      setAiNarrative(String(response?.draft?.narrative || ''));
      setAiNotice('Editable advisory draft generated from aggregate results only. It has not been saved.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not generate the AI narrative.');
    } finally {
      setAiLoading(false);
    }
  };

  const buildActionPlan = async () => {
    if (!summary) return;
    setActionLoading(true);
    setError('');
    setCopyNotice('');
    try {
      const payload = await assignmentsApi.assessmentActionPlan(summary.assignment_id, appliedThreshold, appliedAssignmentIds) as AssessmentActionPlan;
      setActionPlan(payload);
      setGroupSelections(Object.fromEntries(payload.groups.map((group) => [group.group_id, group.students.map((student) => student.student_key)])));
      setGroupDelivery(Object.fromEntries(payload.groups.map((group) => [group.group_id, group.suggested_delivery])));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not build the assessment action plan.');
    } finally {
      setActionLoading(false);
    }
  };

  const copyGroupPrompt = async (group: ActionPlanGroup) => {
    const studentCount = (groupSelections[group.group_id] || []).length;
    const delivery = groupDelivery[group.group_id] === 'independent_lesson_runner'
      ? 'independent student Lesson Runner'
      : 'teacher-led live small group';
    const prompt = group.gpt_prompt_template
      .replace(/{{STUDENT_COUNT}}/g, String(studentCount))
      .replace(/{{DELIVERY_MODE}}/g, delivery);
    try {
      await navigator.clipboard.writeText(prompt);
      setCopyNotice(`Copied an identity-free GPT prompt for “${group.title}”.`);
    } catch {
      setError('Could not copy the prompt. Your browser may have blocked clipboard access.');
    }
  };

  const studentList = (title: string, rows: StudentRow[], detail: (row: StudentRow) => string) => (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{rows.length} student{rows.length === 1 ? '' : 's'}</p>
      {rows.length ? (
        <ul className="mt-3 divide-y divide-gray-100">
          {rows.map((row) => (
            <li key={`${title}-${row.assignment_id || numericAssignmentId}-${row.student_id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <Link className="font-semibold text-indigo-700 hover:underline" to={`/v2/teacher/assignments/${row.assignment_id || numericAssignmentId}/students/${row.student_id}`}>
                {row.student_name}
              </Link>
              <span className="text-gray-600">{row.section_name ? `${row.section_name} · ` : ''}{detail(row)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="mt-3 text-sm text-gray-500">None.</p>}
    </section>
  );

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="teacher"
      navItems={teacherV2NavItems('queue')}
      title="Assessment Class Summary"
    >
      {!redesignEnabled && <TeacherNav activeTab="queue" />}
      <div className={redesignEnabled ? 'mx-auto max-w-6xl space-y-4' : 'mx-auto max-w-6xl space-y-4 p-6'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">One assessment · {(summary?.section_count || 1)} selected section{(summary?.section_count || 1) === 1 ? '' : 's'}</p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-950">{summary?.assessment_title || 'Assessment Class Summary'}</h1>
            {summary && <p className="mt-1 text-sm text-gray-600">{summary.section_name} · {humanize(summary.assessment_stage)} assessment</p>}
          </div>
          <Link to="/v2/teacher/queue?purpose=assessment" className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">Back to Assessment Triage</Link>
        </div>

        <form onSubmit={applyThreshold} className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm font-medium text-gray-900">
            <label htmlFor="objective-threshold">Objective support threshold</label>
            <span className="mt-1 flex items-center gap-2">
              <input id="objective-threshold" type="number" min="0" max="100" step="1" className="w-24 rounded border border-gray-300 px-3 py-2" value={thresholdInput} onChange={(event) => setThresholdInput(event.target.value)} />
              <span>%</span>
            </span>
          </div>
          {(summary?.matching_assignments || []).length > 1 && (
            <fieldset className="w-full border-t border-gray-100 pt-3">
              <legend className="text-sm font-semibold text-gray-900">Sections to include</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {(summary?.matching_assignments || []).map((assignment) => (
                  <label key={assignment.assignment_id} className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={pendingAssignmentIds.includes(assignment.assignment_id)}
                      onChange={(event) => setPendingAssignmentIds((current) => (
                        event.target.checked
                          ? Array.from(new Set([...current, assignment.assignment_id]))
                          : current.filter((id) => id !== assignment.assignment_id)
                      ))}
                    />
                    <span>{assignment.section_name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <button type="submit" disabled={pendingAssignmentIds.length === 0} className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Update summary</button>
          <p className="text-xs text-gray-500">Students and questions below this percentage are flagged for support.</p>
        </form>

        {error && <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
        {loading ? <p className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-500">Loading class summary…</p> : summary && (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Assessment overview">
              <div className="rounded-lg border border-gray-200 bg-white p-4"><p className="text-xs uppercase text-gray-500">Completed</p><p className="mt-1 text-2xl font-semibold">{summary.completion.completed_count}/{summary.completion.targeted_count}</p></div>
              <div className="rounded-lg border border-gray-200 bg-white p-4"><p className="text-xs uppercase text-gray-500">Objective average</p><p className="mt-1 text-2xl font-semibold">{formatPercent(summary.objective_overview.average_percent)}</p></div>
              <div className="rounded-lg border border-gray-200 bg-white p-4"><p className="text-xs uppercase text-gray-500">Below {summary.threshold_percent}%</p><p className="mt-1 text-2xl font-semibold">{summary.students_below_threshold.length}</p></div>
              <div className="rounded-lg border border-gray-200 bg-white p-4"><p className="text-xs uppercase text-gray-500">Auto-scored items</p><p className="mt-1 text-2xl font-semibold">{summary.objective_overview.item_count}</p></div>
            </section>

            <section className="rounded-lg border border-violet-200 bg-violet-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-violet-950">AI-generated narrative</h2>
                  <p className="mt-1 text-xs text-violet-900">Only aggregate counts, percentages, teacher-confirmed ratings, and item labels are sent. No names, roster details, or student response text are sent.</p>
                </div>
                <button type="button" onClick={() => void generateNarrative()} disabled={aiLoading || !summary.ai_narrative.enabled} className="rounded bg-violet-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {aiLoading ? 'Generating…' : aiNarrative ? 'Regenerate narrative' : 'Generate narrative'}
                </button>
              </div>
              {!summary.ai_narrative.enabled && <p className="mt-2 text-xs font-semibold text-violet-800">Privacy-controlled assessment AI is currently disabled.</p>}
              {aiNotice && <p className="mt-2 text-xs text-violet-800">{aiNotice}</p>}
              <textarea aria-label="Editable AI class narrative" className="mt-3 w-full rounded border border-violet-200 bg-white px-3 py-2 text-sm text-gray-900" rows={9} value={aiNarrative} onChange={(event) => setAiNarrative(event.target.value)} placeholder="Generate an editable class narrative after enough evidence has been completed and reviewed." />
            </section>

            <section className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-sky-950">Assessment action plan</h2>
                  <p className="mt-1 text-sm text-sky-900">Suggest editable groups, previously run lessons from this Hook, and identity-free GPT prompts. Nothing is assigned automatically.</p>
                </div>
                <button type="button" onClick={() => void buildActionPlan()} disabled={actionLoading} className="rounded bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {actionLoading ? 'Building…' : actionPlan ? 'Rebuild action plan' : 'Build action plan'}
                </button>
              </div>
              {copyNotice && <p className="mt-3 rounded border border-sky-200 bg-white px-3 py-2 text-sm text-sky-900">{copyNotice}</p>}
              {actionPlan && (
                <div className="mt-4 space-y-4">
                  <p className="text-xs text-sky-900">Recommendations are advisory and use saved assessment evidence. GPT prompts contain learning needs and student counts—not names, identifiers, scores, or response text.</p>
                  {actionPlan.groups.length ? actionPlan.groups.map((group) => {
                    const selected = groupSelections[group.group_id] || [];
                    return (
                      <article key={group.group_id} className="rounded-lg border border-sky-200 bg-white p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">{humanize(group.kind)}</p>
                            <h3 className="mt-1 font-semibold text-gray-950">{group.title}</h3>
                            <p className="mt-1 text-sm text-gray-600">{group.reason}</p>
                            {group.objectives.length > 1 && (
                              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                                {group.objectives.map((objective) => <li key={objective.step_id}>{objective.title}</li>)}
                              </ul>
                            )}
                          </div>
                          <label className="text-sm font-medium text-gray-800">
                            Delivery
                            <select
                              aria-label={`Delivery for ${group.title}`}
                              className="ml-2 rounded border border-gray-300 px-2 py-1.5"
                              value={groupDelivery[group.group_id] || group.suggested_delivery}
                              onChange={(event) => setGroupDelivery((current) => ({ ...current, [group.group_id]: event.target.value as ActionPlanGroup['suggested_delivery'] }))}
                            >
                              <option value="live_small_group">Live small group</option>
                              <option value="independent_lesson_runner">Independent Lesson Runner</option>
                            </select>
                          </label>
                        </div>
                        <fieldset className="mt-3">
                          <legend className="text-xs font-semibold uppercase text-gray-500">Students ({selected.length} selected)</legend>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {group.students.map((student) => (
                              <label key={student.student_key} className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={selected.includes(student.student_key)}
                                  onChange={(event) => setGroupSelections((current) => ({
                                    ...current,
                                    [group.group_id]: event.target.checked
                                      ? Array.from(new Set([...(current[group.group_id] || []), student.student_key]))
                                      : (current[group.group_id] || []).filter((key) => key !== student.student_key),
                                  }))}
                                />
                                <span>{student.student_name}{student.section_name ? ` · ${student.section_name}` : ''}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <div className="mt-3">
                          <p className="text-xs font-semibold uppercase text-gray-500">Previously run lessons in {actionPlan.hook.title}</p>
                          {group.prior_lessons.length ? (
                            <ul className="mt-2 flex flex-wrap gap-2">
                              {group.prior_lessons.map((lesson) => (
                                <li key={lesson.lesson_plan_id}>
                                  <Link className="inline-flex rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-gray-50" to={`/v2/teacher/lesson-plan/${lesson.lesson_plan_id}/edit`}>
                                    {lesson.title} · used {lesson.run_count}×
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          ) : <p className="mt-1 text-sm text-gray-500">No previously run same-Hook lesson has a clear topic match.</p>}
                        </div>
                        <button type="button" disabled={selected.length === 0} onClick={() => void copyGroupPrompt(group)} className="mt-4 rounded bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Copy GPT prompt</button>
                      </article>
                    );
                  }) : <p className="rounded border border-sky-200 bg-white p-3 text-sm text-gray-600">No shared needs formed a group of at least two students. Use individual follow-up below.</p>}

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded border border-gray-200 bg-white p-3">
                      <h3 className="font-semibold text-gray-900">Individual follow-up</h3>
                      <p className="mt-1 text-xs text-gray-500">Students with a need that did not form a group of two or more.</p>
                      {actionPlan.individual_follow_up.length ? <ul className="mt-2 space-y-1 text-sm">{actionPlan.individual_follow_up.map((student) => <li key={student.student_key}>{student.student_name}{student.section_name ? ` · ${student.section_name}` : ''}</li>)}</ul> : <p className="mt-2 text-sm text-gray-500">None.</p>}
                    </div>
                    <div className="rounded border border-gray-200 bg-white p-3">
                      <h3 className="font-semibold text-gray-900">Completion follow-up</h3>
                      <p className="mt-1 text-xs text-gray-500">Kept separate so unfinished work is not treated as a content misconception.</p>
                      {actionPlan.incomplete_students.length ? <ul className="mt-2 space-y-1 text-sm">{actionPlan.incomplete_students.map((student) => <li key={`${student.assignment_id || numericAssignmentId}:${student.student_id}`}>{student.student_name}{student.section_name ? ` · ${student.section_name}` : ''} · {humanize(student.status)}</li>)}</ul> : <p className="mt-2 text-sm text-gray-500">None.</p>}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <h2 className="text-lg font-semibold text-amber-950">Priority areas</h2>
                <p className="mt-1 text-sm text-amber-800">Auto-scored items below {summary.threshold_percent}% across completed assessments.</p>
                {summary.priority_areas.length ? <ul className="mt-3 space-y-2">{summary.priority_areas.map((item) => <li key={item.step_id} className="rounded border border-amber-200 bg-white p-3 text-sm"><p className="font-semibold">{item.title}</p><p className="mt-1 text-gray-600">{formatPercent(item.correct_percent)} correct · {item.incorrect_count} incorrect · {item.unanswered_count} unanswered</p></li>)}</ul> : <p className="mt-3 text-sm text-amber-800">No objective items are below the current threshold.</p>}
              </section>
              <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <h2 className="text-lg font-semibold text-emerald-950">Strengths</h2>
                <p className="mt-1 text-sm text-emerald-800">Auto-scored items meeting or exceeding {summary.threshold_percent}%.</p>
                {summary.strengths.length ? <ul className="mt-3 space-y-2">{summary.strengths.map((item) => <li key={item.step_id} className="rounded border border-emerald-200 bg-white p-3 text-sm"><p className="font-semibold">{item.title}</p><p className="mt-1 text-gray-600">{formatPercent(item.correct_percent)} correct · {item.correct_count}/{summary.completion.completed_count} students</p></li>)}</ul> : <p className="mt-3 text-sm text-emerald-800">No objective items meet the current threshold yet.</p>}
              </section>
            </div>

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="text-lg font-semibold text-gray-900">Teacher-confirmed written evidence quality</h2>
              <p className="mt-1 text-sm text-gray-600">Evidence-quality signals only; these are not official grades.</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {(['exceeds', 'meets', 'approaching', 'beginning', 'not_reviewed'] as const).map((rating) => <div key={rating} className="rounded border border-gray-200 bg-gray-50 p-3"><p className="text-xs uppercase text-gray-500">{humanize(rating)}</p><p className="mt-1 text-xl font-semibold">{summary.written_quality[rating]}</p></div>)}
              </div>
            </section>

            {(summary.section_summaries || []).length > 1 && (
              <section className="overflow-x-auto rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="text-lg font-semibold text-gray-900">Compare selected sections</h2>
                <p className="mt-1 text-xs text-gray-500">The AI narrative uses the anonymous labels shown here; actual section names are not sent.</p>
                <table className="mt-3 min-w-full text-left text-sm">
                  <thead><tr className="border-b border-gray-200 text-xs uppercase text-gray-500"><th className="px-2 py-2">Section</th><th className="px-2 py-2">Completed</th><th className="px-2 py-2">Objective average</th><th className="px-2 py-2">Below threshold</th><th className="px-2 py-2">Written support</th><th className="px-2 py-2">Incomplete</th></tr></thead>
                  <tbody>{(summary.section_summaries || []).map((row) => (
                    <tr key={row.assignment_id} className="border-b border-gray-100 last:border-0">
                      <td className="px-2 py-2 font-semibold text-gray-900">{row.section_name}<span className="ml-2 text-xs font-normal text-gray-500">{row.ai_label}</span></td>
                      <td className="px-2 py-2">{row.completed_count}/{row.targeted_count}</td>
                      <td className="px-2 py-2">{formatPercent(row.objective_average_percent)}</td>
                      <td className="px-2 py-2">{row.below_threshold_count}</td>
                      <td className="px-2 py-2">{row.written_support_count}</td>
                      <td className="px-2 py-2">{row.incomplete_count}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {studentList(`Below ${summary.threshold_percent}% on objective items`, summary.students_below_threshold, (row) => `${formatPercent(row.score_percent)} · ${row.correct_count}/${row.objective_item_count} correct`)}
              {studentList('Written evidence needing support', summary.students_needing_written_support, (row) => (row.ratings || []).map(humanize).join(', '))}
              {studentList('Written evidence not yet rated', summary.students_with_unreviewed_writing, () => 'Teacher rating needed')}
              {studentList('Incomplete assessments', summary.incomplete_students, (row) => humanize(row.status))}
            </div>
          </>
        )}
      </div>
    </OptionalAppShell>
  );
};
